// The AI assistant panel.
//
// Two faces, switched by the tabs under the header, matching the service
// behind it: "Ask" answers questions about this customer's own account, and
// "Help guides" is the browsable index of the same how-to catalogue the
// assistant draws its answers from. It cannot act — no payment is taken and
// nothing is changed from here. When the answer is "here is how", it offers to
// open the screen rather than doing anything on the customer's behalf.
//
// Presentational: the transcript and the mode live in the store
// (store/slices/aiSlice) and the request is driven by ./aiController, so
// closing the panel or navigating away neither loses the conversation nor
// cancels an answer in flight.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CloseIcon } from "icons/close/CloseIcon";
import { AiIcon } from "icons/header/AiIcon";
import { useAppDispatch } from "store/hooks";
import {
  clearAiConversation,
  setAiMode,
  useAiState,
} from "store/slices/aiSlice";
import { AiSuggestions, fetchAiSuggestions } from "./aiApi";
import { abortAiQuestion, sendAiQuestion } from "./aiController";
import { guideFor } from "./guideContent";

interface AiPanelProps {
  /**
   * Whether the drawer is showing. The drawer keeps its children mounted while
   * closed and animates them in, so the panel has to be told — otherwise it
   * would grab focus and fetch suggestions on every page load, for a panel
   * nobody has opened.
   */
  open: boolean;
  onClose: () => void;
  /** The customer's first name, for the greeting. */
  firstName?: string;
}

const AiPanel = ({ open, onClose, firstName }: AiPanelProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { clusterId } = useParams();
  const { messages, busy, mode } = useAiState();

  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState<AiSuggestions | null>(null);
  /** Topic id of the guide expanded in the list; null means all collapsed. */
  const [expandedTopic, setExpandedTopic] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const askTabRef = useRef<HTMLButtonElement>(null);
  const browseTabRef = useRef<HTMLButtonElement>(null);

  // Fetched on first open rather than on mount, and kept afterwards — the list
  // does not change within a session. Both tabs read it: the questions seed
  // the chat's empty state, the topics are the guide index.
  useEffect(() => {
    if (!open || suggestions) return undefined;

    const controller = new AbortController();
    fetchAiSuggestions(controller.signal).then((result) => {
      if (!controller.signal.aborted) setSuggestions(result);
    });

    return () => controller.abort();
  }, [open, suggestions]);

  useEffect(() => {
    if (open && mode === "ask") inputRef.current?.focus();
  }, [open, mode]);

  // Follow the answer as it streams, and land at the bottom of an existing
  // conversation when the panel is reopened or the chat tab comes back.
  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open, mode]);

  const send = (question: string) => {
    if (!question.trim() || busy) return;
    setDraft("");
    sendAiQuestion(question);
  };

  const newChat = () => {
    // Abort first: an answer still streaming would otherwise keep the composer
    // disabled after the transcript it was writing into has been cleared.
    abortAiQuestion();
    dispatch(clearAiConversation());
    setDraft("");
    inputRef.current?.focus();
  };

  const openScreen = (route: string) => {
    navigate(`/${clusterId}${route}`);
    onClose();
  };

  const switchMode = (next: "ask" | "browse") => {
    dispatch(setAiMode(next));
  };

  const greeting = firstName ? `Hi ${firstName}` : "Hi";
  const topics = suggestions?.topics ?? [];

  const tabClass = (selected: boolean) =>
    `px-1 pb-2 text-sm font-medium border-b-2 cursor-pointer ${
      selected
        ? "border-blue text-blue"
        : "border-transparent text-txtSecondary hover:!text-txtPrimary"
    }`;

  return (
    <div
      className="flex flex-col h-full bg-bgPrimary"
      role="dialog"
      aria-label="Assistant"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-strokePrimary shrink-0">
        <div className="flex items-center gap-2">
          <AiIcon strokeColor="var(--color-blue)" width={20} height={20} />
          <p className="text-base font-semibold text-txtPrimary">Assistant</p>
        </div>
        <div className="flex items-center gap-1">
          {mode === "ask" && messages.length > 0 && (
            <button
              type="button"
              onClick={newChat}
              aria-label="Start a new chat"
              className="px-2 py-1 text-sm font-medium text-blue hover:!bg-bgSecondary rounded-md cursor-pointer"
            >
              New chat
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close assistant"
            className="p-1 rounded-md hover:!bg-bgSecondary cursor-pointer"
          >
            <CloseIcon size={20} />
          </button>
        </div>
      </div>

      {/* Mode toggle. Arrow keys move between the two tabs, per the tabs
          pattern; with only two, either arrow just goes to the other one. */}
      <div
        role="tablist"
        aria-label="Assistant mode"
        className="flex items-center gap-4 px-5 pt-3 border-b border-strokePrimary shrink-0"
        onKeyDown={(event) => {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          event.preventDefault();
          const next = mode === "ask" ? "browse" : "ask";
          switchMode(next);
          (next === "ask" ? askTabRef : browseTabRef).current?.focus();
        }}
      >
        <button
          ref={askTabRef}
          type="button"
          role="tab"
          id="ai-tab-ask"
          aria-selected={mode === "ask"}
          aria-controls="ai-panel-ask"
          tabIndex={mode === "ask" ? 0 : -1}
          onClick={() => switchMode("ask")}
          className={tabClass(mode === "ask")}
        >
          Ask
        </button>
        <button
          ref={browseTabRef}
          type="button"
          role="tab"
          id="ai-tab-browse"
          aria-selected={mode === "browse"}
          aria-controls="ai-panel-browse"
          tabIndex={mode === "browse" ? 0 : -1}
          onClick={() => switchMode("browse")}
          className={tabClass(mode === "browse")}
        >
          Help guides
        </button>
      </div>

      {mode === "ask" ? (
        <div
          id="ai-panel-ask"
          role="tabpanel"
          aria-labelledby="ai-tab-ask"
          className="flex flex-col flex-1 min-h-0"
        >
          {/* Transcript. role="log" so answers streaming in — and the error
              shown in their place — are announced by screen readers; visually
              nothing changes. aria-busy defers the announcement until the turn
              settles, instead of re-reading the answer on every token. */}
          <div
            ref={scrollRef}
            role="log"
            aria-label="Conversation"
            aria-busy={busy}
            className="flex-1 overflow-y-auto px-5 py-4"
          >
            {messages.length === 0 ? (
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-sm font-semibold text-txtPrimary">
                    {greeting} — ask me about your account.
                  </p>
                  <p className="text-sm text-txtSecondary mt-1">
                    I can look up your balance, your payments and what is due
                    next, or show you where something is. I can&apos;t take a
                    payment or change anything for you.
                  </p>
                </div>

                {suggestions?.questions?.length ? (
                  <div className="flex flex-col gap-2">
                    {suggestions.questions.map((question) => (
                      <button
                        key={question}
                        type="button"
                        onClick={() => send(question)}
                        className="text-left text-sm text-txtPrimary px-3 py-2 rounded-md border border-strokePrimary hover:!bg-bgSecondary cursor-pointer"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={
                      message.role === "user"
                        ? "flex justify-end"
                        : "flex flex-col"
                    }
                  >
                    {message.role === "user" ? (
                      <div className="max-w-[85%] px-3 py-2 rounded-lg bg-blue text-white text-sm whitespace-pre-wrap break-words">
                        {message.content}
                      </div>
                    ) : (
                      <>
                        <div
                          className={`max-w-[95%] text-sm whitespace-pre-wrap break-words ${
                            message.failed ? "text-red" : "text-txtPrimary"
                          }`}
                        >
                          {message.content || (
                            <span className="text-txtSecondary">Thinking…</span>
                          )}
                        </div>

                        {message.action ? (
                          <div className="mt-3 p-3 rounded-lg bg-bgSecondary">
                            <p className="text-sm font-semibold text-txtPrimary">
                              {message.action.title}
                            </p>
                            <ol className="mt-2 flex flex-col gap-1 list-decimal list-inside">
                              {message.action.steps.map((step) => (
                                <li
                                  key={step}
                                  className="text-sm text-txtSecondary"
                                >
                                  {step}
                                </li>
                              ))}
                            </ol>
                            <button
                              type="button"
                              onClick={() => openScreen(message.action!.route)}
                              className="mt-3 text-sm font-medium text-blue hover:!text-btnPrimaryHover underline cursor-pointer"
                            >
                              Take me there
                            </button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="px-5 py-4 border-t border-strokePrimary shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                rows={1}
                value={draft}
                disabled={busy}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  // Enter sends; Shift+Enter is a newline, as everywhere else.
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    send(draft);
                  }
                }}
                placeholder="Ask about your account…"
                aria-label="Ask about your account"
                className="flex-1 resize-none max-h-32 px-3 py-2 text-sm rounded-md border border-strokePrimary bg-bgPrimary text-txtPrimary placeholder:text-txtSecondary focus:outline-none focus:border-blue disabled:opacity-60"
              />
              <button
                type="button"
                onClick={() => send(draft)}
                disabled={busy || !draft.trim()}
                className="px-4 py-2 text-sm font-medium rounded-md bg-blue text-white hover:!bg-btnPrimaryHover disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                Send
              </button>
            </div>
            <p className="mt-2 text-[11px] leading-4 text-txtSecondary">
              Answers come from your account records. Double-check anything you
              act on, and use Support &amp; Help for anything I can&apos;t do.
            </p>
          </div>
        </div>
      ) : (
        <div
          id="ai-panel-browse"
          role="tabpanel"
          aria-labelledby="ai-tab-browse"
          className="flex-1 overflow-y-auto px-5 py-4"
        >
          <div className="flex flex-col gap-3">
            <p className="text-sm text-txtSecondary">
              Step-by-step guides to the portal. Pick one to see how it works,
              or ask me instead.
            </p>
            {topics.length ? (
              <div className="flex flex-col gap-2">
                {topics.map((topic) => {
                  const guide = guideFor(topic);
                  const expanded = expandedTopic === topic.topic;
                  return (
                    <div
                      key={topic.topic}
                      className="rounded-md border border-strokePrimary overflow-hidden"
                    >
                      <button
                        type="button"
                        // A guide expands in place; a topic with nothing to
                        // show (unknown to both the service and the built-in
                        // copies) still does the useful thing and opens its
                        // screen directly.
                        onClick={() =>
                          guide
                            ? setExpandedTopic(expanded ? null : topic.topic)
                            : openScreen(topic.route)
                        }
                        aria-expanded={guide ? expanded : undefined}
                        className="w-full text-left px-3 py-2.5 hover:!bg-bgSecondary cursor-pointer"
                      >
                        <p className="text-sm font-medium text-txtPrimary">
                          {topic.title}
                        </p>
                        {guide?.description ? (
                          <p className="text-sm text-txtSecondary mt-0.5">
                            {guide.description}
                          </p>
                        ) : null}
                      </button>
                      {expanded && guide ? (
                        <div className="px-3 pb-3 flex flex-col gap-2 border-t border-strokePrimary pt-2.5">
                          <ol className="flex flex-col gap-1.5 list-decimal list-inside">
                            {guide.steps.map((step) => (
                              <li key={step} className="text-sm text-txtPrimary">
                                {step}
                              </li>
                            ))}
                          </ol>
                          <button
                            type="button"
                            onClick={() => openScreen(topic.route)}
                            className="self-start px-4 py-2 text-sm font-medium rounded-md bg-blue text-white hover:!bg-btnPrimaryHover cursor-pointer"
                          >
                            Take me there
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-txtSecondary">
                Guides aren&apos;t available right now — ask me on the Ask tab
                instead.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AiPanel;
